import { lazy } from 'react';

import Browser from '@wetransfer/transfer/modules/concorde-browser';
import Actions from '@wetransfer/transfer/store/action';
import Store from '@wetransfer/transfer/store';

// Static pages with code
import '@wetransfer/transfer/page/home';
import '@wetransfer/transfer/page/sign-in';
import '@wetransfer/transfer/page/plus';
import '@wetransfer/transfer/page/help';
import '@wetransfer/transfer/page/legal';
import '@wetransfer/transfer/page/products';

import { checkIfSSOIsEnabled } from '@wetransfer/transfer/auth-bundle/helpers/sso';
import StaticPage from '@wetransfer/transfer/component/panel/content';
import { AUTH_STATE } from '@wetransfer/transfer/constant/auth';
import { FF_REFERRAL_PROGRAM } from '@wetransfer/transfer/constant/experiments';
import {
  ACCOUNT_PAYMENT,
  PRICING,
  SIGN_UP,
  USER_ONBOARDING,
} from '@wetransfer/transfer/constant/routes';
import {
  shouldAskBeforeLeaving,
  createOnRouteHandler,
} from '@wetransfer/transfer/helper/route';
import upgradeRedirect, {
  signUpRedirect,
} from '@wetransfer/transfer/helper/router/upgradeRedirect';
import {
  isLoggedInFreeUser,
  isLoggedOutOrFreeUser,
  userHasFreeTier,
  userCanManageSubscription,
} from '@wetransfer/transfer/helper/user';
import ErrorHandler from '@wetransfer/transfer/modules/error-handler';
import { getVariant } from '@wetransfer/transfer/modules/experiments';
import I18n from '@wetransfer/transfer/modules/i18n';
import Router from '@wetransfer/transfer/modules/router';
import CcpaPreferencesBannerExperiment from '@wetransfer/transfer/page/legal/ccpa-preferences-banner-experiment';
import ConsentManager from '@wetransfer/transfer/page/legal/consent-manager';
import { PAID_TRANSFERS_ALPHA_BUYER_SURVEY_ROUTE } from '@wetransfer/transfer/page/PaidTransfersBuyerSurveyPage';
import { didUserSeePaidTransfersAlphaBuyerSurvey } from '@wetransfer/transfer/page/PaidTransfersBuyerSurveyPage/helpers';
import {
  advancedReceivingVariant,
  micrositesVariant,
  monetizationVariant,
} from '@wetransfer/transfer/page/PublishSneakPeek/constants';
import { getPublishSmokeTestFlagVariant } from '@wetransfer/transfer/page/PublishSneakPeek/utils';
import { shouldShowPublishSurvey } from '@wetransfer/transfer/page/PublishSurveyPage/helpers';
import Preview from '@wetransfer/transfer/page/transfer/preview';
import VerifyMobile from '@wetransfer/transfer/page/VerifyMobile/VerifyMobile';

const navigateToHomePage = () => {
  Router.navigateTo('/', true);
};

const navigateToPricingPanel = () => {
  Router.navigateTo('/pricing');
};

const navigateToLoginPage = (path) => {
  Router.navigateTo('/log-in');
  Store.dispatch(Actions.Session.setRedirect(path));
};

const hasInvoices = () => {
  const state = Store.getState();

  return state.invoices?.list && Boolean(state.invoices?.list.length);
};

const redirectToLoginOrHomePage = (path, user, account) => {
  if (!user.loggedIn) {
    navigateToLoginPage(path);

    return;
  }

  if (userHasFreeTier(user) || !userCanManageSubscription(account)) {
    navigateToHomePage();
  }
};

/**
 * A route can either be a string or an object with the following properties (not all properties are documented):
 * - `route` (required): The path.
 * - `component` (required): Which component to render on this route.
 * - `name`: The name of the route.
 * - `title`: The document title to use for this route.
 * - `shouldRedirect`: The user either has permission to view the page or not, a returned true value calls redirectTo, false let's them continue to the intended path.
 * - `redirectTo`: The route the user redirects to if they do not have permissions
 * `requiredAuthState`:
 *   - Set this to `AUTH_STATE.LOGGED_IN` if the route is only accessible to logged in users.
 *   - Set this to `AUTH_STATE.LOGGED_OUT` if the route is only accessible to logged out users.
 *   - Leave this undefined if users can access this route regardless of their logged state.
 */

export const getPageRoutes = () => [
  // Static pages
  { route: '/legal/advertising', component: StaticPage },
  { route: '/legal/disclosure', component: StaticPage },
  { route: '/legal/privacy', component: StaticPage },
  { route: '/legal/manage-cookies', component: ConsentManager },
  {
    route: '/legal/ccpa',
    component: CcpaPreferencesBannerExperiment,
  },
  { route: '/legal/takedown', component: StaticPage },
  { route: '/legal/terms', component: StaticPage },
  {
    route: PRICING.route,
    name: PRICING.name,
    title: I18n.t('site.titles.pricing'),
    component: lazy(() =>
      import(
        /* webpackChunkName: "pricing" */ '@wetransfer/transfer/page/Pricing/Pricing'
      )
    ),
  },
  {
    route: '/verify-mobile',
    component: VerifyMobile,
    shouldRedirect: () => !Browser.isMobile,
    redirectTo: (router) => navigateToHomePage(router),
    raw: true,
  },
  // Payment pages
  {
    route: USER_ONBOARDING.route,
    name: USER_ONBOARDING.name,
    component: lazy(() =>
      import(
        /* webpackChunkName: "UniversalOnboarding" */ '@wetransfer/transfer/page/UniversalOnboarding/UniversalOnboarding'
      )
    ),
    shouldRedirect: (user) => isLoggedOutOrFreeUser(user),
    redirectTo: (path, user) => redirectToLoginOrHomePage(path, user),
    requiredAuthState: AUTH_STATE.LOGGED_IN,
  },
  {
    route: '/payment/methods',
    name: 'payment-methods',
    component: lazy(() =>
      import(
        /* webpackChunkName: "payment-methods" */ '@wetransfer/transfer/page/payment-methods'
      )
    ),
    shouldRedirect: (user) => isLoggedOutOrFreeUser(user),
    redirectTo: (path, user) => redirectToLoginOrHomePage(path, user),
    requiredAuthState: AUTH_STATE.LOGGED_IN,
    payment: true,
  },
  {
    route: '/payment/update',
    name: 'payment-update',
    component: lazy(() =>
      import(
        /* webpackChunkName: "payment-update" */ '@wetransfer/transfer/page/payment-update'
      )
    ),
    shouldRedirect: (user) => isLoggedOutOrFreeUser(user),
    redirectTo: (path, user) => redirectToLoginOrHomePage(path, user),
    requiredAuthState: AUTH_STATE.LOGGED_IN,
    payment: true,
  },
  {
    route: '/payment/pending',
    name: 'payment-pending',
    component: lazy(() =>
      import(
        /* webpackChunkName: "payment-pending" */ '@wetransfer/transfer/page/payment-pending'
      )
    ),
    shouldRedirect: (user) => !user.loggedIn,
    redirectTo: (path) => navigateToLoginPage(path),
    requiredAuthState: AUTH_STATE.LOGGED_IN,
    payment: true,
  },
  {
    route: SIGN_UP.route,
    component: lazy(() =>
      import(
        /* webpackChunkName: "sign-up" */ '@wetransfer/transfer/page/sign-up-page'
      )
    ),
    name: SIGN_UP.name,
    title: I18n.t('site.titles.sign_up'),
    shouldRedirect: (user) => signUpRedirect(user).shouldRedirect,
    redirectTo: (_path, user) =>
      Router.navigateTo(signUpRedirect(user).route, true),
    requiredAuthState: AUTH_STATE.LOGGED_OUT,
  },
  {
    route: '/upgrade',
    name: 'upgrade',
    component: lazy(() =>
      import(
        /* webpackChunkName: "upgrade" */ '@wetransfer/transfer/page/upgrade'
      )
    ),
    shouldRedirect: (user) => upgradeRedirect(user).shouldRedirect,
    redirectTo: (_path, user) =>
      Router.navigateTo(upgradeRedirect(user).route, true),
    requiredAuthState: AUTH_STATE.LOGGED_IN,
    payment: true,
  },
];

export const init = () => {
  const transferRoutes = ['/wallpaper/:advert_id'];

  const pageRoutes = getPageRoutes();

  const plusRoutes = [
    {
      route: '/help',
      component: StaticPage,
      name: 'help-section',
      title: I18n.t('site.titles.help'),
    },
    {
      route: '/log-in',
      component: StaticPage,
      name: 'signin-page',
      title: I18n.t('site.titles.sign_in'),
      shouldRedirect: (user) => !checkIfSSOIsEnabled && user.loggedIn,
      redirectTo: () => navigateToHomePage(),
      requiredAuthState: AUTH_STATE.LOGGED_OUT,
    },
    {
      route: '/forgot-password',
      component: lazy(() =>
        checkIfSSOIsEnabled()
          ? import(
              /* webpackChunkName: "forgotPassword-auth" */ '@wetransfer/transfer/auth-bundle/page/ForgotPassword'
            )
          : import(
              /* webpackChunkName: "forgotpassword" */ '@wetransfer/transfer/page/forgotpassword'
            )
      ),
      title: I18n.t('site.titles.forgot_password'),
    },
    {
      route: '/ter-optout',
      component: lazy(() =>
        import(
          /* webpackChunkName: "unsubscribe-expiration" */ '@wetransfer/transfer/page/unsubscribeexpiration'
        )
      ),
    },
    {
      route: '/pm-optout',
      component: lazy(() =>
        import(
          /* webpackChunkName: "unsubscribe-onboarding" */ '@wetransfer/transfer/page/unsubscribe-onboarding'
        )
      ),
    },
    {
      route: '/profile',
      component: lazy(() =>
        import(
          /* webpackChunkName: "profile" */ '@wetransfer/transfer/page/profile'
        )
      ),
      shouldRedirect: (user) => !user.loggedIn,
      redirectTo: () => navigateToLoginPage('/profile'),
      requiredAuthState: AUTH_STATE.LOGGED_IN,
    },
    {
      route: '/account/callback',
      component: lazy(() =>
        import(
          /* webpackChunkName: "auth-bundle-account" */ '@wetransfer/transfer/auth-bundle/page/AccountCallback'
        )
      ),
      shouldRedirect: () => !checkIfSSOIsEnabled(),
      redirectTo: () => navigateToLoginPage('/'),
    },
    {
      route: '/account/referral',
      component: lazy(() =>
        import(
          /* webpackChunkName: "account" */ '@wetransfer/transfer/page/account'
        )
      ),
      shouldRedirect: (user) =>
        isLoggedOutOrFreeUser(user) || getVariant(FF_REFERRAL_PROGRAM) !== 'on',
      redirectTo: (path) => navigateToLoginPage(path),
      requiredAuthState: AUTH_STATE.LOGGED_IN,
    },
    {
      route: '/contacts',
      component: lazy(() =>
        import(
          /* webpackChunkName: "contact-list" */ '@wetransfer/transfer/page/contact/list'
        )
      ),
      shouldRedirect: (user) => !user.loggedIn,
      redirectTo: (path) => navigateToLoginPage(path),
      requiredAuthState: AUTH_STATE.LOGGED_IN,
    },
    {
      route: '/transfers',
      component: lazy(() =>
        import(
          /* webpackChunkName: "transfer-list" */ '@wetransfer/transfer/page/TransferList/TransferListExperimentGate'
        )
      ),
      shouldRedirect: (user) => !user.loggedIn,
      redirectTo: (path) => navigateToLoginPage(path),
      requiredAuthState: AUTH_STATE.LOGGED_IN,
    },
    {
      route: '/transfers/:group/:transferId',
      component: lazy(() =>
        import(
          /* webpackChunkName: "transfer-detail" */ '@wetransfer/transfer/page/transfer/detail'
        )
      ),
      shouldRedirect: (user) => !user.loggedIn,
      redirectTo: (path) => navigateToLoginPage(path),
      requiredAuthState: AUTH_STATE.LOGGED_IN,
    },
    {
      route: '/payment/retry',
      component: lazy(() =>
        import(
          /* webpackChunkName: "complete-order" */ '@wetransfer/transfer/page/complete-order'
        )
      ),
      shouldRedirect: (user) => isLoggedOutOrFreeUser(user),
      redirectTo: (path, user) => redirectToLoginOrHomePage(path, user),
      requiredAuthState: AUTH_STATE.LOGGED_IN,
      payment: true,
    },

    {
      route: '/transfer/progress',
      component: lazy(() =>
        import(
          /* webpackChunkName: "transfer-progress-upsell" */ '@wetransfer/transfer/page/transfer/progress-upsell'
        )
      ),
      name: 'page-transfer-progress-details',
    },
    {
      route: '/ltdn-optout',
      component: lazy(() =>
        import(
          /* webpackChunkName: "ltdn-optout" */ '@wetransfer/transfer/page/link-transfer-unsubscribe'
        )
      ),
      auth: true,
    },
    {
      route: '/account/two-factor-authentication/enable',
      name: 'two-factor-authentication-enable',
      component: lazy(() =>
        import(
          /* webpackChunkName: "two-factor-authentication-enable" */ '@wetransfer/transfer/page/two-factor-authentication/enable'
        )
      ),
      shouldRedirect: (user) => !user.loggedIn,
      redirectTo: (path) => navigateToLoginPage(path),
      requiredAuthState: AUTH_STATE.LOGGED_IN,
    },
  ];

  // if viewing non mobile version, root path is transfers
  if (!Browser.isMobile) {
    transferRoutes.push('/');
    transferRoutes.push('/downloads/:id/:secret?');

    // /downloads/:id/:recipient_id/grid does NOT match this route
    transferRoutes.push('/downloads/:id/:recipient_id/:secret(\\w{6})');
    transferRoutes.push('/welcome');

    transferRoutes.push({
      route: '/account-verification',
      shouldRedirect: (user) => !user.loggedIn,
      redirectTo: (path) => navigateToLoginPage(path),
      requiredAuthState: AUTH_STATE.LOGGED_IN,
    });

    plusRoutes.push({
      route: '/downloads/:id/:recipient_id?/:secret/preview/:file_id',
      component: Preview,
      name: 'preview-panel',
      updateTransfer: true,
      requiresTOC: true,
      requiresTransferPassword: true,
    });
    plusRoutes.push({
      route: '/downloads/:id/:recipient_id?/:secret/grid',
      component: lazy(() =>
        import(
          /* webpackChunkName: "transfer-grid" */ '@wetransfer/transfer/page/transfer/grid'
        )
      ),
      name: 'grid-panel',
      updateTransfer: true,
      requiresTOC: true,
      requiresTransferPassword: true,
    });

    pageRoutes.push({
      route: '/branding',
      component: lazy(() =>
        import(
          /* webpackChunkName: "branding" */ '@wetransfer/transfer/page/profile'
        )
      ),
      shouldRedirect: (user) => !user.loggedIn,
      redirectTo: () => navigateToLoginPage('/branding'),
      requiredAuthState: AUTH_STATE.LOGGED_IN,
    });

    pageRoutes.push({
      route: ACCOUNT_PAYMENT.route,
      component: lazy(() =>
        import(
          /* webpackChunkName: "account" */ '@wetransfer/transfer/page/account'
        )
      ),
      shouldRedirect: (user) => isLoggedOutOrFreeUser(user),
      redirectTo: (path, user) => redirectToLoginOrHomePage(path, user),
      requiredAuthState: AUTH_STATE.LOGGED_IN,
    });

    pageRoutes.push({
      route: '/account',
      component: lazy(() =>
        import(
          /* webpackChunkName: "account" */ '@wetransfer/transfer/page/account'
        )
      ),
      shouldRedirect: (user) => !user.loggedIn,
      redirectTo: (path) => navigateToLoginPage(path),
      requiredAuthState: AUTH_STATE.LOGGED_IN,
    });
    pageRoutes.push({
      route: '/account/security',
      component: lazy(() =>
        import(
          /* webpackChunkName: "account" */ '@wetransfer/transfer/page/account'
        )
      ),
      shouldRedirect: (user) => !user.loggedIn,
      redirectTo: (path) => navigateToLoginPage(path),
      requiredAuthState: AUTH_STATE.LOGGED_IN,
    });

    pageRoutes.push({
      route: '/account/emails',
      component: lazy(() =>
        import(
          /* webpackChunkName: "account" */ '@wetransfer/transfer/page/account'
        )
      ),
      shouldRedirect: (user) => !user.loggedIn,
      redirectTo: (path) => navigateToLoginPage(path),
      requiredAuthState: AUTH_STATE.LOGGED_IN,
    });

    pageRoutes.push({
      route: '/account/integrations',
      component: lazy(() =>
        import(
          /* webpackChunkName: "account" */ '@wetransfer/transfer/page/account'
        )
      ),
      shouldRedirect: (user) => isLoggedOutOrFreeUser(user),
      redirectTo: (path, user, account) =>
        redirectToLoginOrHomePage(path, user, account),
      requiredAuthState: AUTH_STATE.LOGGED_IN,
    });
    pageRoutes.push({
      route: '/account/payment/cancel',
      component: lazy(() =>
        import(
          /* webpackChunkName: "payment-cancel" */ '@wetransfer/transfer/page/CancelSubscription/CancelSubscription'
        )
      ),
      shouldRedirect: (user) => isLoggedOutOrFreeUser(user),
      redirectTo: (path, user) => redirectToLoginOrHomePage(path, user),
      requiredAuthState: AUTH_STATE.LOGGED_IN,
    });
    pageRoutes.push({
      route: '/account/payment/cancel-reason',
      component: lazy(() =>
        import(
          /* webpackChunkName: "payment-cancel" */ '@wetransfer/transfer/page/payment-cancel-reason'
        )
      ),
      shouldRedirect: (user) => isLoggedOutOrFreeUser(user),
      redirectTo: (path, user) => redirectToLoginOrHomePage(path, user),
      requiredAuthState: AUTH_STATE.LOGGED_IN,
    });
    pageRoutes.push({
      route: '/account/billing',
      component: lazy(() =>
        import(
          /* webpackChunkName: "account" */ '@wetransfer/transfer/page/account'
        )
      ),
      shouldRedirect: (user, _, account) =>
        isLoggedOutOrFreeUser(user) || !userCanManageSubscription(account),
      redirectTo: (path, user, account) =>
        redirectToLoginOrHomePage(path, user, account),
      requiredAuthState: AUTH_STATE.LOGGED_IN,
    });
    pageRoutes.push({
      route: '/account/renew-billing',
      component: lazy(() =>
        import(
          /* webpackChunkName: "account" */ '@wetransfer/transfer/page/account'
        )
      ),
      shouldRedirect: (user) => isLoggedOutOrFreeUser(user),
      redirectTo: (path, user) => redirectToLoginOrHomePage(path, user),
      requiredAuthState: AUTH_STATE.LOGGED_IN,
      payment: true,
    });
    pageRoutes.push({
      route: '/account/invoices',
      component: lazy(() =>
        import(
          /* webpackChunkName: "account" */ '@wetransfer/transfer/page/account'
        )
      ),
      shouldRedirect: (user, _, account) =>
        !hasInvoices() || !user.loggedIn || !userCanManageSubscription(account),
      redirectTo: (path, user, account) =>
        redirectToLoginOrHomePage(path, user, account),
      requiredAuthState: AUTH_STATE.LOGGED_IN,
    });

    // Paid Transfers
    transferRoutes.push('/p/:id/:secret?');
    transferRoutes.push('/p/:id/:recipient_id/:secret(\\w{6})');
    plusRoutes.push({
      route: '/p/:id/:recipient_id?/:secret/preview/:file_id',
      component: Preview,
      name: 'preview-panel',
      updateTransfer: true,
      requiresTOC: true,
      requiresTransferPassword: true,
    });
    plusRoutes.push({
      route: '/p/:id/:recipient_id?/:secret/grid',
      component: lazy(() =>
        import(
          /* webpackChunkName: "transfer-grid" */ '@wetransfer/transfer/page/transfer/grid'
        )
      ),
      name: 'grid-panel',
      updateTransfer: true,
      requiresTOC: true,
      requiresTransferPassword: true,
    });
  } else {
    const TransferWindow = lazy(() =>
      import(
        /* webpackChunkName: "transfer" */ '@wetransfer/transfer/component/TransferWindow'
      )
    );

    plusRoutes.push(
      {
        route: '/branding',
        component: lazy(() =>
          import(
            /* webpackChunkName: "branding" */ '@wetransfer/transfer/page/profile'
          )
        ),
        shouldRedirect: (user) => !user.loggedIn,
        redirectTo: () => navigateToLoginPage('/branding'),
        requiredAuthState: AUTH_STATE.LOGGED_IN,
      },
      {
        route: '/account',
        component: lazy(() =>
          import(
            /* webpackChunkName: "account" */ '@wetransfer/transfer/page/account'
          )
        ),
        shouldRedirect: (user) => !user.loggedIn,
        redirectTo: (path) => navigateToLoginPage(path),
        requiredAuthState: AUTH_STATE.LOGGED_IN,
      },
      {
        route: '/account/security',
        component: lazy(() =>
          import(
            /* webpackChunkName: "account" */ '@wetransfer/transfer/page/account'
          )
        ),
        shouldRedirect: (user) => !user.loggedIn,
        redirectTo: (path) => navigateToLoginPage(path),
        requiredAuthState: AUTH_STATE.LOGGED_IN,
      },
      {
        route: '/account/integrations',
        component: lazy(() =>
          import(
            /* webpackChunkName: "account" */ '@wetransfer/transfer/page/account'
          )
        ),
        shouldRedirect: (user) => isLoggedOutOrFreeUser(user),
        redirectTo: (path, user, account) =>
          redirectToLoginOrHomePage(path, user, account),
        requiredAuthState: AUTH_STATE.LOGGED_IN,
      },
      {
        route: '/account/payment/cancel',
        component: lazy(() =>
          import(
            /* webpackChunkName: "payment-cancel" */ '@wetransfer/transfer/page/CancelSubscription/CancelSubscription'
          )
        ),
        shouldRedirect: (user) => isLoggedOutOrFreeUser(user),
        redirectTo: (path, user) => redirectToLoginOrHomePage(path, user),
        requiredAuthState: AUTH_STATE.LOGGED_IN,
      },
      {
        route: '/account/payment/cancel-reason',
        component: lazy(() =>
          import(
            /* webpackChunkName: "payment-cancel" */ '@wetransfer/transfer/page/payment-cancel-reason'
          )
        ),
        shouldRedirect: (user) => isLoggedOutOrFreeUser(user),
        redirectTo: (path, user) => redirectToLoginOrHomePage(path, user),
        requiredAuthState: AUTH_STATE.LOGGED_IN,
      },
      {
        route: '/account/billing',
        component: lazy(() =>
          import(
            /* webpackChunkName: "account" */ '@wetransfer/transfer/page/account'
          )
        ),
        shouldRedirect: (user, _, account) =>
          isLoggedOutOrFreeUser(user) || !userCanManageSubscription(account),
        redirectTo: (path, user, account) =>
          redirectToLoginOrHomePage(path, user, account),
        requiredAuthState: AUTH_STATE.LOGGED_IN,
      },
      {
        route: '/account/renew-billing',
        component: lazy(() =>
          import(
            /* webpackChunkName: "account" */ '@wetransfer/transfer/page/account'
          )
        ),
        shouldRedirect: (user) => isLoggedOutOrFreeUser(user),
        redirectTo: (path, user) => redirectToLoginOrHomePage(path, user),
        requiredAuthState: AUTH_STATE.LOGGED_IN,
        payment: true,
      },
      {
        route: '/account/invoices',
        component: lazy(() =>
          import(
            /* webpackChunkName: "account" */ '@wetransfer/transfer/page/account'
          )
        ),
        shouldRedirect: (user, _, account) =>
          !hasInvoices() ||
          !user.loggedIn ||
          !userCanManageSubscription(account),
        redirectTo: (path, user, account) =>
          redirectToLoginOrHomePage(path, user, account),
        requiredAuthState: AUTH_STATE.LOGGED_IN,
      }
    );

    pageRoutes.push({
      route: '/',
      component: StaticPage,
      name: 'home-mobile',
    });

    pageRoutes.push({
      route: '/upload',
      component: TransferWindow,
      name: 'transfer-mobile',
    });

    pageRoutes.push({
      route: '/account-verification',
      component: TransferWindow,
      shouldRedirect: (user) => !user.loggedIn,
      redirectTo: (path) => navigateToLoginPage(path),
      requiredAuthState: AUTH_STATE.LOGGED_IN,
      name: 'transfer-mobile',
    });

    pageRoutes.push({
      route: '/downloads/:id/:secret',
      component: TransferWindow,
    });

    pageRoutes.push({
      route: '/downloads/:id/:recipient_id?/:secret/preview/:file_id',
      component: TransferWindow,
    });

    pageRoutes.push({
      route: '/downloads/:id/:recipient_id?/:secret/preview',
      component: TransferWindow,
    });

    pageRoutes.push({
      route: '/downloads/:id/:recipient_id/:secret(\\w{6})',
      component: TransferWindow,
    });

    pageRoutes.push({
      route: '/downloads/:id/:recipient_id?/:secret/grid',
      component: TransferWindow,
      name: 'grid-panel',
      updateTransfer: true,
    });

    // Paid download transfer routes render the same upload component but with a prefix
    pageRoutes.push({
      route: '/p/:id/:secret',
      component: TransferWindow,
    });

    pageRoutes.push({
      route: '/p/:id/:recipient_id?/:secret/preview/:file_id',
      component: TransferWindow,
    });

    pageRoutes.push({
      route: '/p/:id/:recipient_id?/:secret/preview',
      component: TransferWindow,
    });

    pageRoutes.push({
      route: '/p/:id/:recipient_id/:secret(\\w{6})',
      component: TransferWindow,
    });

    pageRoutes.push({
      route: '/p/:id/:recipient_id?/:secret/grid',
      component: TransferWindow,
      name: 'grid-panel',
      updateTransfer: true,
    });
  }

  pageRoutes.push({
    route: '/sign-up/completed',
    component: lazy(() =>
      import(
        /* webpackChunkName: "UniversalOnboarding" */ '@wetransfer/transfer/page/UniversalOnboarding/UniversalOnboarding'
      )
    ),
    name: 'free-sign-up-completed',
    shouldRedirect: (user) => !isLoggedInFreeUser(user) || !user.isVerified,
    redirectTo: (router) => navigateToHomePage(router),
    requiredAuthState: AUTH_STATE.LOGGED_IN,
  });

  // This route still is using the old onboarding flow to drive users to update their domain and wallpaper
  // This is accessible from the premium onboarding flow on the last step after clicking "Create Page"
  pageRoutes.push({
    route: '/pro-onboarding',
    name: 'pro-onboarding',
    component: lazy(() =>
      import(
        /* webpackChunkName: "payment" */ '@wetransfer/transfer/page/onboarding-wallpaper'
      )
    ),
    shouldRedirect: (user) => isLoggedOutOrFreeUser(user),
    redirectTo: (path, user) => redirectToLoginOrHomePage(path, user),
    requiredAuthState: AUTH_STATE.LOGGED_IN,
  });

  if (!Browser.isMobile) {
    // Publish smoke test
    const publishSmokeTestVariant = getPublishSmokeTestFlagVariant();

    if (publishSmokeTestVariant) {
      switch (publishSmokeTestVariant.name) {
        case micrositesVariant.name:
          plusRoutes.push({
            route: micrositesVariant.route,
            name: micrositesVariant.name,
            component: lazy(() =>
              import(
                /* webpackChunkName: "quick-pages-sneak-peek" */ '@wetransfer/transfer/page/MicrositesSneakPeek'
              )
            ),
          });
          break;

        case monetizationVariant.name:
          plusRoutes.push({
            route: monetizationVariant.route,
            name: monetizationVariant.name,
            component: lazy(() =>
              import(
                /* webpackChunkName: "pay-to-download-sneak-peek" */ '@wetransfer/transfer/page/MonetizationSneakPeek'
              )
            ),
          });
          break;

        case advancedReceivingVariant.name:
          plusRoutes.push({
            route: advancedReceivingVariant.route,
            name: advancedReceivingVariant.name,
            component: lazy(() =>
              import(
                /* webpackChunkName: "advanced-receiving-sneak-peek" */ '@wetransfer/transfer/page/AdvancedReceivingSneakPeek'
              )
            ),
          });
          break;

        default:
          break;
      }
    }
  }

  if (!Browser.isMobile && shouldShowPublishSurvey()) {
    plusRoutes.push({
      route: '/publish-survey',
      name: 'publish-survey',
      component: lazy(() =>
        import(
          /* webpackChunkName: "publish-survey" */ '@wetransfer/transfer/page/PublishSurveyPage'
        )
      ),
    });
  }

  if (!Browser.isMobile && !didUserSeePaidTransfersAlphaBuyerSurvey()) {
    plusRoutes.push({
      route: PAID_TRANSFERS_ALPHA_BUYER_SURVEY_ROUTE,
      name: 'paid-transfers-buyer-survey',
      component: lazy(() =>
        import(
          /* webpackChunkName: "paid-transfers-buyer-survey" */ '@wetransfer/transfer/page/PaidTransfersBuyerSurveyPage'
        )
      ),
    });
  }

  pageRoutes.push({
    route: '/pro',
    name: 'plus-page',
    shouldRedirect: () => true,
    redirectTo: () => navigateToPricingPanel(),
  });

  // Configure all available routes
  // Start the router
  const router = new Router({
    // The following routes affect the Transfer component
    transfer: transferRoutes,

    // The following routes affect the Page component, mid-width
    plus: plusRoutes,

    // The following routes affect the Page component, full-width
    page: pageRoutes,
  });

  // Let's see what we need to do for this new route
  router.on('route', createOnRouteHandler());

  // Catch errors / "404" = redirect to root
  router.on('error', (path) => {
    ErrorHandler.track(
      {
        name: 'UnknownRoute',
        message: 'This route does not exist.',
        stack: 'init/routing',
      },
      { path: path }
    );

    router.resolve('/', false);
  });

  router.on('beforeroute', (complete) => {
    const { route, transfer } = Store.getState();

    // Prompt for something however you want and make sure that the complete
    // function is called when the user wants to continue.
    if (shouldAskBeforeLeaving(route, transfer)) {
      Store.dispatch(Actions.Route.setNextAction(complete));
    } else {
      complete();
    }

    // You MUST return false if you manually control when to continue
    return false;
  });
  router.start(window.location.pathname, {
    hijackLinks: true,
  });
};
